import { useEffect } from "react";
import Button from "../Button/Button";

export type ToastTone = "success" | "error" | "info";

export type ToastState = {
  message: string;
  tone: ToastTone;
} | null;

type ToastProps = {
  isOpen: boolean;
  message: string;
  tone?: ToastTone;
  durationMs?: number;
  onClose: () => void;
};

const Toast = ({ isOpen, message, tone = "info", durationMs = 6000, onClose }: ToastProps) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = window.setTimeout(() => {
      onClose();
    }, durationMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [durationMs, isOpen, onClose]);

  if (!isOpen || !message) {
    return null;
  }

  const toneClasses =
    tone === "success"
      ? "border-emerald-200 bg-[linear-gradient(135deg,_rgba(236,253,245,1),_rgba(209,250,229,0.92))] text-emerald-900"
      : tone === "error"
        ? "border-red-200 bg-[linear-gradient(135deg,_rgba(254,242,242,1),_rgba(254,226,226,0.92))] text-red-900"
        : "border-myteengig-border bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(234,248,255,0.98))] text-myteengig-text-primary";

  const accentClasses =
    tone === "success" ? "bg-emerald-500" : tone === "error" ? "bg-red-500" : "bg-myteengig-primary";

  const toneLabel = tone === "success" ? "Success" : tone === "error" ? "Action needed" : "Heads up";

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[60] w-full max-w-sm">
      <div
        className={`pointer-events-auto overflow-hidden rounded-2xl border shadow-[0_18px_48px_rgba(10,70,102,0.18)] [animation:toast-enter_220ms_ease-out] motion-reduce:animate-none ${toneClasses}`}
        role="status"
        aria-live="polite"
      >
        <div className={`h-1.5 w-full ${accentClasses}`} />
        <div className="flex items-start gap-4 p-4">
          <div className={`mt-1 h-3 w-3 shrink-0 rounded-full ${accentClasses}`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-80">{toneLabel}</p>
            <p className="mt-1 text-sm font-medium leading-6">{message}</p>
          </div>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            className="px-2 py-0 text-current opacity-80 hover:bg-transparent hover:opacity-100"
            onClick={onClose}
            aria-label="Dismiss notification"
          >
            x
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
